import React, { useEffect, useState } from "react";
import { Crown, Check, X, ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { isNativeApp } from "../lib/platform";

/**
 * Listens for `bmai:upgrade-required` events (dispatched by the API
 * interceptor on 402 responses) and shows a friendly upgrade modal
 * with one-tap navigation to the billing page.
 */
export default function UpgradeModalListener() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onUpgrade = (e) => {
      setMessage(e.detail?.message || "Pro subscription required.");
      setOpen(true);
    };
    window.addEventListener("bmai:upgrade-required", onUpgrade);
    return () => window.removeEventListener("bmai:upgrade-required", onUpgrade);
  }, []);

  if (!open) return null;

  const close = () => setOpen(false);
  const goToBilling = () => {
    setOpen(false);
    navigate("/app/billing");
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="upgrade-modal"
      onClick={close}
    >
      <div
        className="relative w-full max-w-md bg-[#121212] border border-[#6B9BFF]/30 p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 text-white/40 hover:text-white"
          data-testid="upgrade-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="w-12 h-12 flex items-center justify-center border border-[#6B9BFF]/40 bg-[#6B9BFF]/5 mb-5">
          <Crown className="w-6 h-6 text-[#6B9BFF]" />
        </div>

        <div className="label-eyebrow text-[#6B9BFF] mb-2">Pro feature</div>
        <h3 className="font-display text-3xl leading-tight mb-3">
          {isNativeApp() ? "Pro feature" : "Your free trial has ended."}
        </h3>
        <p className="text-sm text-white/70 mb-5 leading-relaxed" data-testid="upgrade-modal-detail">
          {isNativeApp()
            ? "This feature is included with DAIONE Pro. Sign in on the web to manage your subscription — Pro features unlock here automatically."
            : message}
        </p>

        <ul className="space-y-2 text-sm text-white/70 mb-6">
          {[
            "Unlimited AI workout plan generation",
            "Photo-based meal logging (Claude Vision)",
            "AI micro-workouts from the muscle heatmap",
            "AI food estimator",
          ].map((b) => (
            <li key={b} className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-[#6B9BFF] shrink-0" /> {b}
            </li>
          ))}
        </ul>

        <div className="flex flex-col sm:flex-row gap-2">
          {isNativeApp() ? (
            // Apple App Store rules: no in-app link to external payment.
            <button
              onClick={close}
              className="flex-1 bg-[#6B9BFF] text-black font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-white inline-flex items-center justify-center gap-2"
              data-testid="upgrade-modal-dismiss-native"
            >
              Got it
            </button>
          ) : (
            <>
              <button
                onClick={goToBilling}
                className="flex-1 bg-[#6B9BFF] text-black font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-white inline-flex items-center justify-center gap-2"
                data-testid="upgrade-modal-cta"
              >
                <Sparkles className="w-4 h-4" /> Upgrade — $9.99 AUD <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={close}
                className="border border-white/15 text-white/70 hover:text-white text-xs uppercase tracking-widest font-bold px-5 py-3"
                data-testid="upgrade-modal-dismiss"
              >
                Not now
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
